import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';
import { UserList } from '../../_models/user.model';

@Injectable({
  providedIn: 'root'
})
export class ExcelExportService {
  
  exportMembersToExcel(members: UserList[], fileName: string, title: string): void {
    // Create worksheet data
    const worksheetData = this.prepareWorksheetData(members, title);
    
    // Create workbook
    const workbook = XLSX.utils.book_new();
    
    // Create worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    
    // Set column widths
    const colWidths = [
      { wch: 10 }, // ID
      { wch: 15 }, // Prénom
      { wch: 15 }, // Nom
      { wch: 25 }, // Email
      { wch: 15 }, // Téléphone
      { wch: 30 }, // Adresse
      { wch: 20 }, // Date de création
    ];
    worksheet['!cols'] = colWidths;
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Membres');
    
    // Generate Excel file
    XLSX.writeFile(workbook, fileName);
  }
  
  private prepareWorksheetData(members: UserList[], title: string): any[][] {
    const worksheetData: any[][] = [];
    
    // Add title row
    worksheetData.push([title]);
    worksheetData.push([]); // Empty row
    
    // Add metadata rows
    const exportDate = new Date().toLocaleString('fr-BE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    worksheetData.push(['Date d\'exportation:', exportDate]);
    worksheetData.push(['Nombre total de membres:', members.length]);
    worksheetData.push([]); // Empty row
    
    // Add headers row
    worksheetData.push([
      'ID',
      'Prénom',
      'Nom',
      'Email',
      'Téléphone',
      'Adresse',
      'Date de création'
    ]);
    
    // Add data rows
    members.forEach(member => {
      worksheetData.push([
        member.id,
        member.firstName || '',
        member.lastName || '',
        member.email,
        this.formatPhoneNumber(member.phoneNumber),
        this.formatAddress(member.address),
        this.formatDate(member.createdAt)
      ]);
    });
    
    // Add summary row
    worksheetData.push([]); // Empty row
    worksheetData.push(['Export généré par Farco Security']);
    
    return worksheetData;
  }
  
  private formatPhoneNumber(phoneNumber: string): string {
    if (!phoneNumber) return '';
    
    // Format Belgian phone numbers: +32 4XX XX XX XX or 04XX XX XX XX
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    if (cleaned.startsWith('32') && cleaned.length === 11) {
      // International format +32
      return `+${cleaned.substring(0, 2)} ${cleaned.substring(2, 5)} ${cleaned.substring(5, 7)} ${cleaned.substring(7, 9)} ${cleaned.substring(9, 11)}`;
    } else if (cleaned.startsWith('4') && cleaned.length === 10) {
      // National format 04
      return `${cleaned.substring(0, 3)} ${cleaned.substring(3, 5)} ${cleaned.substring(5, 7)} ${cleaned.substring(7, 9)} ${cleaned.substring(9, 11)}`;
    }
    
    return phoneNumber;
  }
  
  private formatAddress(address: any): string {
    if (!address) return '';
    
    const parts: string[] = [];
    
    if (address.streetNumber && address.street) {
      parts.push(`${address.street} ${address.streetNumber}`);
    } else if (address.street) {
      parts.push(address.street);
    }
    
    if (address.postalCode && address.city) {
      parts.push(`${address.postalCode} ${address.city}`);
    } else if (address.postalCode) {
      parts.push(address.postalCode);
    } else if (address.city) {
      parts.push(address.city);
    }
    
    return parts.join(', ');
  }
  
  private formatDate(dateString: string): string {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-BE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  
  generateFileName(): string {
    const now = new Date();
    const dateStr = now.toISOString()
      .replace(/[:.]/g, '-')
      .replace('T', '_')
      .slice(0, 19);
    
    return `farco-membres-${dateStr}.xlsx`;
  }
}